import { useMemo, useState, useEffect, useCallback } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { Icon, Button, useToast } from "@elaniintc/goes-design-system";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/utils/theme";
import {
  useGetInsolvenciesInfinite,
  useRejectAppeal,
} from "@/queries/users/insolvencies.api";
import type {
  AppealDocumentType,
  InsolvencyType,
} from "@/types/entities/insolvencies";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  parseAmount,
  getDaysSinceCreated,
  getStatusConfig,
  convertUnitToDays,
  isInsolvencyDisabled,
  getPaidOnBehalfOf,
} from "./insolvency.utils";
import type { RegistryType } from "@/schemas/digital-files";
import { InsolvencyCancelModal } from "@/components/insolvencies/insolvency-cancel-modal";
import InsolvencyFormModal from "@/pages/users/external/details/actions/insolvency-form-modal";
import {
  renderComments,
  renderSubjectAvatar,
} from "./insolvency-list-modal.helpers";
import { InsolvencyDetail } from "./insolvency-detail";

export function InsolvencyListModal({
  isOpen,
  onClose,
  userId,
  userName,
  registry,
  expedientsIds,
  showActions = false,
  onAdd,
  onUpdate,
  onRejectAppeal,
}: Readonly<{
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  registry?: RegistryType;
  expedientsIds?: string[];
  userName?: string;
  showActions?: boolean;
  onAdd?: () => void;
  onUpdate?: (item: InsolvencyType) => void;
  onRejectAppeal?: (item: InsolvencyType) => void;
}>) {
  const { showToast } = useToast();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRejectAppealOpen, setIsRejectAppealOpen] = useState(false);
  const [rejectAppealComment, setRejectAppealComment] = useState("");

  const {
    data: insolvenciesResponse,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useGetInsolvenciesInfinite(
    userId ?? "",
    registry ? [registry.id] : expedientsIds,
    {
      ...(registry ? { insolvencyable_type: "expedient_base_registry" } : {}),
    },
    isOpen ||
      (!!registry?.unpaid_insolvencies_count &&
        registry?.unpaid_insolvencies_count > 0),
  );

  const isExpedientMode = useMemo(() => !!registry, [registry]);

  const data = useMemo(
    () => insolvenciesResponse?.pages.flatMap((page) => page.data) ?? [],
    [insolvenciesResponse],
  );

  // Refetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  // Intersection observer for infinite scroll
  const [sentinelRef, isIntersecting] = useIntersectionObserver({
    threshold: 0,
  });

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (isIntersecting) {
      handleLoadMore();
    }
  }, [isIntersecting, handleLoadMore]);

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const selected = useMemo(
    () => data.find((infraction) => infraction.id === selectedId) ?? data[0],
    [data, selectedId],
  );

  const selectedPaidOnBehalfOf = useMemo(
    () => (selected ? getPaidOnBehalfOf(selected) : undefined),
    [selected],
  );

  useEffect(() => {
    if (data.length > 0 && !selectedId) {
      setSelectedId(data[0].id);
    }
  }, [data, selectedId]);

  const statusConfig = selected ? getStatusConfig(selected) : null;
  // Calculate if the selected infraction is overdue
  const isOverdue = selected
    ? getDaysSinceCreated(selected.created_at) >=
      convertUnitToDays(
        selected.payment_deadline_value,
        selected.payment_deadline_unit,
      )
    : false;

  const isAnularDisabled =
    !selected ||
    isOverdue ||
    !["ACTIVE", "APPEAL_SUBMITTED", "APPEAL_REJECTED"].includes(
      selected.status,
    );

  const rejectAppealMutation = useRejectAppeal(selected?.id ?? "");

  const handleRejectAppeal = async () => {
    if (!rejectAppealComment.trim()) return;
    try {
      await rejectAppealMutation.mutateAsync({
        reason: selected?.reason ?? "",
        comments: rejectAppealComment,
      });
      showToast({
        title: "Apelación rechazada",
        description: "La apelación ha sido marcada como inválida.",
        options: { type: "success", duration: 5000 },
      });
      setIsRejectAppealOpen(false);
      setRejectAppealComment("");
      refetch();
    } catch {
      showToast({
        title: "Error al rechazar apelación",
        description: "No se pudo rechazar la apelación. Inténtalo de nuevo.",
        options: { type: "error", duration: 5000 },
      });
    }
  };

  // Filter comments into two arrays
  const otherComments =
    selected?.comments?.filter(
      (comment) => comment.created_by !== userId && comment.created_by !== null,
    ) ?? [];

  const userComments =
    selected?.comments?.filter(
      (comment) => comment.created_by === userId || comment.created_by === null,
    ) ?? [];

  const handleDownload = async (document: AppealDocumentType) => {
    try {
      window.open(document.url, "_blank", "noopener,noreferrer");
      showToast({
        title: "Documento descargado",
        description: "El documento ha sido descargado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch {
      showToast({
        title: "Error al descargar documento",
        description: "No se pudo descargar el documento. Inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const handleAddClick = () => (onAdd ? onAdd() : setIsAddModalOpen(true));

  const handleUpdateClick = () =>
    onUpdate ? onUpdate(selected) : setIsEditModalOpen(true);

  const handleRejectClick = () =>
    onRejectAppeal ? onRejectAppeal(selected) : setIsCancelModalOpen(true);

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent type="modal" className="max-w-3xl px-6">
          <div className="flex items-center justify-center py-8">
            <p className="text-body-quiet">Cargando infracciones...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent type="modal" className="max-w-3xl px-6">
          <div className="flex items-center justify-center py-8">
            <p className="text-danger">Error al cargar infracciones</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!data?.length) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent type="modal" className="max-w-3xl px-6">
          <DialogHeader className="mb-3">
            <DialogTitle className="text-h5 font-semibold text-heading">
              Infracciones
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <p className="text-body-quiet">No hay infracciones registradas</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!selected) {
    return null;
  }

  const comments = renderComments(otherComments);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        type="modal"
        className="max-w-3xl p-0 gap-0"
        showCloseButton={false}
      >
        <DialogHeader className="py-4 px-8 flex-row w-full justify-between items-center">
          <DialogTitle className="text-h5 font-semibold text-heading">
            Infracciones
          </DialogTitle>
          <DialogDescription className="sr-only">
            Lista y detalle de infracciones
          </DialogDescription>
          <DialogPrimitive.Close asChild>
            <Button
              type="button"
              size="icon"
              variant="quiet"
              aria-label="Cerrar"
              data-testid="insolvency-list-modal-close"
            >
              <Icon name="close" type="fas" />
            </Button>
          </DialogPrimitive.Close>
        </DialogHeader>
        <div className="grid md:grid-cols-2 grid-cols-1 border-t border-neutral-silent max-h-125">
          <div className="flex flex-col px-4 py-6 border-r border-neutral-silent col-span-1 overflow-y-auto">
            <div>
              {data.map((item) => {
                const isDisabled = isInsolvencyDisabled(item);

                return (
                  <div key={item.id} className="mb-1">
                    <button
                      type="button"
                      onClick={() => setSelectedId(item.id)}
                      data-testid={`insolvency-list-item-${item.id}`}
                      className={cn(
                        "group flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors cursor-pointer",
                        item.id === selectedId
                          ? "bg-surface-action-accent"
                          : "bg-surface hover:border-neutral-quiet hover:bg-neutral-object-accent",
                        isDisabled &&
                          "bg-neutral-object-accent-silent border-2 border-transparent opacity-60",
                        isDisabled &&
                          item.id === selectedId &&
                          "border-neutral-quiet",
                      )}
                    >
                      <span className="flex items-center gap-3">
                        {renderSubjectAvatar(item)}
                        <span className="flex flex-col min-w-0">
                          <span
                            className={cn(
                              "text-body font-medium max-w-52.5 truncate",
                              item.id === selectedId && !isDisabled
                                ? "text-action"
                                : "text-body-quiet",
                            )}
                          >
                            {item.reason}
                            {item.reference && (
                              <span className="font-normal text-xs ml-1 opacity-70">
                                ({item.reference})
                              </span>
                            )}
                          </span>
                        </span>
                      </span>

                      <span
                        className={cn(
                          "text-body-quiet text-right",
                          item.id === selectedId && !isDisabled
                            ? "text-action"
                            : "text-body-quiet",
                        )}
                      >
                        ${parseAmount(item.amount).toFixed(2)}
                      </span>
                    </button>
                  </div>
                );
              })}
              {/* Sentinel element for infinite scroll */}
              {hasNextPage && <div ref={sentinelRef} className="py-2" />}
              {isFetchingNextPage && (
                <div className="flex justify-center py-2">
                  <Icon
                    type="far"
                    name="spinner-third"
                    className="animate-spin text-on-disabled"
                  />
                </div>
              )}
            </div>
            {showActions && (
              <Button
                type="button"
                variant="tertiary"
                size="default"
                onClick={handleAddClick}
                data-testid="insolvency-add-button"
                className={cn(
                  "mt-2",
                  isExpedientMode ? "w-fit self-start" : "w-full",
                )}
              >
                <Icon type="fas" name="plus" className="text-sm" size="sm" />
                Añadir infracción
              </Button>
            )}
          </div>
          <InsolvencyDetail
            selected={selected}
            statusConfig={statusConfig}
            selectedPaidOnBehalfOf={selectedPaidOnBehalfOf}
            comments={comments}
            userComments={userComments}
            isExpedientMode={isExpedientMode}
            isRejectAppealOpen={isRejectAppealOpen}
            setIsRejectAppealOpen={setIsRejectAppealOpen}
            rejectAppealComment={rejectAppealComment}
            setRejectAppealComment={setRejectAppealComment}
            rejectAppealMutation={rejectAppealMutation}
            handleRejectAppeal={handleRejectAppeal}
            handleRejectClick={handleRejectClick}
            handleDownload={handleDownload}
            showActions={showActions}
          />
        </div>
        <InsolvencyFormModal
          isOpen={isAddModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            refetch();
          }}
          userId={userId || ""}
          userName={userName || ""}
        />
        <InsolvencyFormModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            refetch();
          }}
          userId={userId || ""}
          userName={userName || ""}
          mode="edit"
          insolvencyData={selected}
        />
        <InsolvencyCancelModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          infractionId={selected?.id || null}
          reason={selected?.reason || ""}
        />
        {showActions && (
          <section className="flex justify-end items-center gap-3 border-t border-neutral-quiet py-4 px-4">
            <Button
              type="button"
              variant="danger-tertiary"
              size="default"
              disabled={isAnularDisabled}
              onClick={handleRejectClick}
            >
              Anular infracción
            </Button>
            <Button
              type="button"
              variant="default"
              size="default"
              disabled={isAnularDisabled}
              onClick={handleUpdateClick}
            >
              Editar
            </Button>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default InsolvencyListModal;
